import React from 'react';
import { useTheme, THEMES } from './ThemeProvider';

const ThemeSelector = () => {
  const { currentTheme, setTheme, effectsEnabled, toggleEffects, autoEnabledTheme } = useTheme();

  // Group themes by category
  const holidayThemes = Object.values(THEMES).filter(t => t.category === 'holiday');
  const neonThemes = Object.values(THEMES).filter(t => t.category === 'neon');
  const defaultTheme = THEMES.default;

  const ThemeCard = ({ theme }) => {
    const isActive = currentTheme === theme.id;
    const isAutoEnabled = autoEnabledTheme === theme.id;
    const isNeon = theme.category === 'neon';

    return (
      <div
        onClick={() => setTheme(theme.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          backgroundColor: isActive
            ? (isNeon ? '#1a1a1a' : theme.colors?.primary + '22' || '#e3f2fd')
            : '#fff',
          border: isActive
            ? `2px solid ${theme.colors?.primary || '#1976d2'}`
            : '1px solid #ddd',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          position: 'relative',
          boxShadow: isActive && isNeon && theme.colors?.glow
            ? theme.colors.glow.replace(/30px/g, '5px').replace(/20px/g, '3px').replace(/10px/g, '2px')
            : 'none'
        }}
      >
        <span style={{ fontSize: '28px' }}>{theme.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: '600',
            color: isActive && isNeon ? theme.colors?.primary : '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {theme.name}
            {isAutoEnabled && (
              <span style={{
                fontSize: '11px',
                backgroundColor: '#4caf50',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '10px'
              }}>
                In Season
              </span>
            )}
          </div>
          <div style={{
            fontSize: '12px',
            color: isActive && isNeon ? theme.colors?.primary + 'aa' : '#666',
            marginTop: '2px'
          }}>
            {theme.description}
          </div>
        </div>
        {isActive && (
          <span style={{
            color: theme.colors?.primary || '#1976d2',
            fontSize: '20px'
          }}>
            ✓
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Effects Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '15px 20px',
        backgroundColor: effectsEnabled ? '#e8f5e9' : '#f5f5f5',
        borderRadius: '8px',
        border: effectsEnabled ? '2px solid #4caf50' : '1px solid #ddd'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>✨</span>
          <div>
            <div style={{ fontWeight: '600', color: '#333' }}>Visual Effects</div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Animated effects like snow, confetti, fireworks
            </div>
          </div>
        </div>
        <button
          onClick={toggleEffects}
          style={{
            padding: '8px 16px',
            backgroundColor: effectsEnabled ? '#4caf50' : '#9e9e9e',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          {effectsEnabled ? 'On' : 'Off'}
        </button>
      </div>

      {/* Default Theme */}
      <div>
        <h3 style={{
          margin: '0 0 12px 0',
          color: '#333',
          fontSize: '14px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Default
        </h3>
        <ThemeCard theme={defaultTheme} />
      </div>

      {/* Holiday Themes */}
      <div>
        <h3 style={{
          margin: '0 0 12px 0',
          color: '#333',
          fontSize: '14px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Holiday Themes
        </h3>
        <div style={{ display: 'grid', gap: '10px' }}>
          {holidayThemes.map(theme => (
            <ThemeCard key={theme.id} theme={theme} />
          ))}
        </div>
      </div>

      {/* Neon/Surveying Themes */}
      <div>
        <h3 style={{
          margin: '0 0 12px 0',
          color: '#333',
          fontSize: '14px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Neon / Surveying Themes
        </h3>
        <div style={{ display: 'grid', gap: '10px' }}>
          {neonThemes.map(theme => (
            <ThemeCard key={theme.id} theme={theme} />
          ))}
        </div>
      </div>

      {/* Info box */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fff3e0',
        borderRadius: '8px',
        border: '1px solid #ffcc02',
        fontSize: '13px',
        color: '#666'
      }}>
        <strong>💡 Pro tip:</strong> Holiday themes automatically activate during their season,
        but you can switch to any theme anytime! Neon themes work great at night or in low-light conditions.
      </div>
    </div>
  );
};

export default ThemeSelector;
